import { Button, MarkdownRenderer, Text } from '@aurora-components';
import { type FragmentType, getFragmentData } from '@gql';
import { CardPaymentForm } from './card-payment-form';
import {
  BankTransferSectionWrapper,
  CardsWrapper,
  PaymentCardTitleWrapper,
  PaymentSectionWrapper,
  PaymentTitleWrapper,
  PointsSectionWrapper,
} from './payment-section.styles';
import { VildarPaymentFragment, VildarPaymentSectionFragment } from './payment-section-gql';

type Props = FragmentType<typeof VildarPaymentSectionFragment>;

function PointsSection({
  title,
  description,
  href,
  buttonLabel,
}: {
  title?: string | null;
  description?: string | null;
  href?: string | null;
  buttonLabel?: string | null;
}) {
  return (
    <PointsSectionWrapper>
      <PaymentCardTitleWrapper>
        <Text $variant="h5" $color="primary-base100" $noBalancer $truncateVertical>
          {title}
        </Text>
        <Text $variant="p" $color="primary-base100" $noBalancer $truncateVertical>
          {description}
        </Text>
      </PaymentCardTitleWrapper>
      {href && buttonLabel && (
        <Button as="a" href={href} target="_blank">
          {buttonLabel}
        </Button>
      )}
    </PointsSectionWrapper>
  );
}

function BankTransferSection({ title, description }: { title?: string | null; description?: string | null }) {
  return (
    <BankTransferSectionWrapper>
      <PaymentCardTitleWrapper>
        <Text $variant="h5" $color="primary-base100" $noBalancer $truncateVertical>
          {title}
        </Text>
        {description && (
          <MarkdownRenderer
            $variant="span"
            $color="primary-base100"
            $fontWeight="Light"
            $noBalancer
            markdown={description}
          />
        )}
      </PaymentCardTitleWrapper>
    </BankTransferSectionWrapper>
  );
}

export function PaymentSection(props: Props) {
  const { payment_section: paymentSection } = getFragmentData(VildarPaymentSectionFragment, props);
  const edges = paymentSection?.paymentConnection?.edges;
  const node = edges?.find((edge) => edge?.node)?.node;

  if (!node) return null;

  const paymentFragment = getFragmentData(VildarPaymentFragment, node);

  if (!paymentFragment) return null;

  const { display_title, description, card_payment, points, bank_transfer } = paymentFragment;

  return (
    <PaymentSectionWrapper>
      {display_title || description ? (
        <PaymentTitleWrapper>
          {display_title && (
            <Text $variant="h5" $color="primary-base100" $noBalancer $truncateVertical>
              {display_title}
            </Text>
          )}
          {description && (
            <Text $variant="p" $color="primary-base100" $noBalancer $truncateVertical>
              {description}
            </Text>
          )}
        </PaymentTitleWrapper>
      ) : null}
      <CardsWrapper>
        <CardPaymentForm
          title={card_payment?.title}
          description={card_payment?.description}
          options={card_payment?.amount}
          inputLabel={card_payment?.input_label}
          buttonLabel={card_payment?.button_label}
        />
        <PointsSection
          title={points?.title}
          description={points?.description}
          href={points?.external_url?.href}
          buttonLabel={points?.external_url?.title}
        />
        <BankTransferSection title={bank_transfer?.title} description={bank_transfer?.description} />
      </CardsWrapper>
    </PaymentSectionWrapper>
  );
}
